import { Button, PageModal } from '@dev-spendesk/grapes';
import { type ReactNode, useState } from 'react';

import { Stepper } from 'src/core/common/components/Stepper';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import {
  type AnalyticalFieldExportSettings,
  usePutAnalyticalFieldExportSettings,
} from 'src/core/modules/accounting-integration/apis/useAnalyticalFieldExportSettings';
import {
  NotificationType,
  useNotifications,
} from 'src/core/modules/app/notifications/hooks/useNotifications';

import { CompareExportSettings } from './steps/CompareExportSettings';
import { LearnHowToChangeSettings } from './steps/LearnHowToChangeSettings';
import { UpdateSettings } from './steps/UpdateSettings';
import {
  type InternalValue,
  type AnalyticalFieldMappingV2,
} from '../../analyticalCodeMappings';

type Props = {
  exportLevel: 'header' | 'headerLine';
  fieldType: 'customField' | 'costCenter' | 'expenseCategory';
  isOpen: boolean;
  onClose(): void;
  fieldMapping: AnalyticalFieldMappingV2 | undefined;
  fieldExportSetting?: AnalyticalFieldExportSettings | undefined;
  internalField: InternalValue;
  isSplittable: boolean;
};

type Steps =
  | 'compare_export_settings'
  | 'learn_how_to_change_settings'
  | 'update_settings';

export const LineSplitOnboardingGuideModal = (props: Props) => {
  if (!props.isOpen) {
    return null;
  }

  return <LineSplitOnboardingGuide {...props} />;
};

export const LineSplitOnboardingGuide = ({
  onClose,
  exportLevel,
  fieldType,
  fieldMapping,
  fieldExportSetting,
  internalField,
  isSplittable,
}: Props) => {
  const { t } = useTranslation('global');
  const [currentStep, setCurrentStep] = useState<Steps>(
    'compare_export_settings',
  );
  const [hasError, setHasError] = useState(false);
  const [selectedExportSettings, setSelectedExportSettings] = useState<
    AnalyticalFieldExportSettings | undefined
  >(fieldExportSetting);
  const { updateCompanyExportSettingsAsync, isPending: isSubmitting } =
    usePutAnalyticalFieldExportSettings();

  const { pushNotif } = useNotifications();

  let field = internalField.name;
  if (fieldType === 'costCenter') {
    field = t(
      'accounting-integration.exportSettingsOnboardingGuide.fieldNames.costCenter.fieldNamePlural',
    );
  }
  if (fieldType === 'expenseCategory') {
    field = t(
      'accounting-integration.exportSettingsOnboardingGuide.fieldNames.expenseCategory.fieldNamePlural',
    );
  }
  const integration = 'Netsuite';

  const stepNames: Steps[] = [
    'compare_export_settings',
    'learn_how_to_change_settings',
    'update_settings',
  ];
  const stepLabels = [
    t(
      'accounting-integration.exportSettingsOnboardingGuide.compareExportSettings.step',
    ),
    t(
      'accounting-integration.exportSettingsOnboardingGuide.learnToChangeSettings.step',
    ),
    t(
      'accounting-integration.exportSettingsOnboardingGuide.updateSettings.step',
    ),
  ];
  const effectiveSettings = selectedExportSettings ?? fieldExportSetting;
  const updateSettingsHandler = () => {
    if (!effectiveSettings?.sendOnHeader && !effectiveSettings?.sendOnLine) {
      setHasError(true);
      return;
    }
    if (!fieldMapping) {
      return;
    }
    // Capture result in async way
    (async () => {
      const result = await updateCompanyExportSettingsAsync([
        {
          externalId: fieldMapping.externalId,
          sendOnHeader: effectiveSettings.sendOnHeader,
          sendOnLine: effectiveSettings.sendOnLine,
        },
      ]);
      if (!result.length) {
        // It's a failure
        return;
      }

      pushNotif({
        type: NotificationType.Success,
        message: t(
          'accounting-integration.exportSettings.notification.success.title',
          { integration, field },
        ),
      });
      setHasError(false);
      onClose();
    })();
  };

  const steps: { [k in Steps]: ReactNode } = {
    compare_export_settings: (
      <CompareExportSettings
        fieldType={fieldType}
        exportLevel={exportLevel}
        actions={
          <>
            <Button
              iconName="chevron-right"
              iconPosition="right"
              text={stepLabels[1]}
              onClick={() => setCurrentStep('learn_how_to_change_settings')}
            />
            <Button
              variant="secondaryNeutral"
              text={t('misc.cancel')}
              onClick={onClose}
            />
          </>
        }
      />
    ),
    learn_how_to_change_settings: (
      <LearnHowToChangeSettings
        exportLevel={exportLevel}
        actions={
          <>
            <Button
              iconName="chevron-right"
              iconPosition="right"
              text={stepLabels[2]}
              onClick={() => setCurrentStep('update_settings')}
            />
            <Button
              variant="secondaryNeutral"
              iconName="chevron-left"
              text={t('misc.back')}
              onClick={() => setCurrentStep('compare_export_settings')}
            />
          </>
        }
      />
    ),
    update_settings: (
      <UpdateSettings
        internalField={internalField}
        fieldType={fieldType}
        fieldMapping={fieldMapping}
        fieldExportSetting={fieldExportSetting}
        hasError={hasError}
        isSplittable={isSplittable}
        onChange={(settings) => {
          setSelectedExportSettings(settings);
          setHasError(false);
        }}
        actions={
          <>
            <Button
              variant="secondaryNeutral"
              iconName="chevron-left"
              text={t('misc.back')}
              onClick={() => setCurrentStep('learn_how_to_change_settings')}
            />
            <Button
              text={t(
                'accounting-integration.exportSettingsOnboardingGuide.updateSettings.finishSetup',
              )}
              onClick={updateSettingsHandler}
              isLoading={isSubmitting}
            />
          </>
        }
      />
    ),
  };

  return (
    <PageModal
      isOpen
      onClose={onClose}
      title={
        <Stepper
          className="StepperNoBorder [&_header]:-z-10 [&_header]:border-none"
          stepsTitles={stepLabels}
          currentStepIndex={stepNames.indexOf(currentStep)}
        >
          <div />
        </Stepper>
      }
    >
      {steps[currentStep]}
    </PageModal>
  );
};
